import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import {
  ArrowRightIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ArrowTopRightOnSquareIcon
} from '@heroicons/react/24/solid';
import { useAuth } from '../context/AuthContext';

import landing1 from '../assets/landing1.png';
// Assuming you have these other images in the same folder
import landing2 from '../assets/landing2.png';
import landing3 from '../assets/landing3.png';
import landing4 from '../assets/landing4.png';
import landing5 from '../assets/landing5.png';
import landing6 from '../assets/landing6.png';
import landing7 from '../assets/landing7.png';
import landing8 from '../assets/landing8.png';
import landing9 from '../assets/landing9.png';
import landing10 from '../assets/landing10.png';

import Header from '../components/Header';
import Footer from '../components/Footer';
import howto1 from '../assets/howto1.png';
import howto2 from '../assets/howto2.png';

import amesLogo from '../assets/ames-logo.svg';

const HowItWorksSection = () => {
  const steps = [
    {
      title: 'Profile',
      subtitle: 'Data Collection',
      description: 'Capture precise pavement profile data using your Ames Profiler on-site.',
      image: howto1,
    },
    {
      title: 'Export',
      subtitle: 'From Desktop',
      description: 'Easily export your raw data from the Ames Profiler desktop software.',
      image: howto2,
    },
    {
      title: 'Upload',
      subtitle: 'To Cloud',
      description: 'Securely upload your exported data to Ames Connect for processing and analysis.',
      image: landing1,
    },
  ];

  return (
    <section id="how-it-works-visual" className="pt-24 pb-10 bg-white scroll-mt-20">
      <div className="w-full mx-auto px-8 sm:px-10 lg:px-12 text-left">
        <h2 className="text-5xl text-gray-900 mb-6">How It Works</h2>
        <p className="text-lg text-gray-600 max-w-3xl mb-16">
          A seamless flow from field data collection to cloud-based collaboration.
          Integrate your existing workflow with the power of Ames Connect.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {steps.map((step, index) => (
            <div
              key={index}
              className="relative aspect-square overflow-hidden
                         group cursor-pointer transform transition-transform duration-300 hover:scale-[1.01]
                         hover:shadow-lg border border-gray-100"
            >
              <img
                src={step.image}
                // --- SEO UPDATE: Changed alt tag from step.title to step.description ---
                alt={step.description}
                className="absolute inset-0 w-full h-full object-cover transition-opacity duration-300 group-hover:opacity-80"
              />

              <div className="absolute inset-0 bg-black/50"></div>

              <div className="absolute top-4 left-4 text-white text-xl z-10 uppercase font-mono">
                {step.title}
              </div>
              
              <div className="absolute top-4 right-4 font-mono text-white text-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10">
                {step.subtitle}
              </div>

              <div className="absolute bottom-4 left-4 font-mono right-4 text-white text-sm text-left z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-300 leading-tight">
                {step.description}
              </div>
            </div>
          ))}

          {/* --- Added 4th Item for the Button --- */}
          <div className="relative aspect-square flex flex-col items-center justify-center text-center p-6 border border-gray-300">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              Ready to Start?
            </h3>
            <p className="text-sm text-gray-600 mb-6">
              Reach out to our team today.
            </p>
            <Link 
              to="/feature-highlight" 
              className="bg-gray-900 text-white px-8 py-3 font-semibold text-md inline-flex items-center hover:bg-gray-800 transition-colors"
            >
              View Capabilities
            </Link>
          </div>

        </div>
      </div>
    </section>
  );
}

const heroImages = [
  landing1,
  landing2,
  landing3,
  landing4,
  landing5,
  landing6,
];

// --- SEO UPDATE: Added descriptive alt text for the carousel images ---
const heroImageAlts = [
  'Ames Connect dashboard showing pavement segment analysis and roughness data.',
  '3D KML visualization of a road, color-coded by roughness, in Ames Connect.',
  'Ames Connect interface displaying a detailed ALR (Areas of Localized Roughness) summary table.',
  'Side-by-side comparison of two different road profiler sessions in Ames Connect.',
  'The file management and secure cloud storage system within Ames Connect.',
  'A map view in Ames Connect showing GIS data overlaid with pavement quality metrics.',
];

const FADE_INTERVAL_MS = 4000; // 4 seconds

export default function LandingPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('segment');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Effect for hero image carousel
  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % heroImages.length);
    }, FADE_INTERVAL_MS);

    return () => clearTimeout(timer);
  }, [currentImageIndex]);

  const location = useLocation();

  // Add this Effect to handle the scroll
  useEffect(() => {
    if (location.state && location.state.scrollTo) {
      const element = document.getElementById(location.state.scrollTo);
      if (element) {
        // A small timeout ensures the DOM is fully painted before scrolling
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    }
  }, [location]);


  if (loading) {
    return <div className="min-h-screen bg-white"></div>;
  }

  const features = {
    segment: {
      title: 'Intelligent Segment Analysis',
      description: 'Automatically divide profiler runs into meaningful segments based on road conditions to prioritize maintenance and budget allocation.',
      image: landing1,
    },
    alr: {
      title: 'Areas of Localized Roughness ',
      description: 'Automatically detect and flag areas that exceed roughness thresholds, complete with severity metrics for targeted repairs.',
      image: landing7,
    },
    kml: {
      title: '3D KML Visualization',
      description: 'Experience your data in three dimensions. Visualize elevation and roughness in an intuitive 3D environment.',
      image: landing2,
    },
    alrsummary: {
      title: 'ALR Summary Table',
      description: 'Get a comprehensive overview of all detected ALRs with key metrics like severity, length, and location in an easy-to-read table format.',
      image: landing10,
    },
    compare: {
      title: 'Session Comparison Tools',
      description: 'Easily compare multiple sessions side-by-side to track road condition changes over time and assess maintenance effectiveness.',
      image: landing9,
    },
    filestorage: {
      title: 'Secure Cloud File Storage',
      description: 'Store and manage all your profiler data securely in the cloud, accessible anytime from anywhere.',
      image: landing8,
    },
  }

  const currentFeature = features[activeTab];

  return (
    <div className="min-h-screen bg-white text-gray-800">

      <Header />

      <main className="pt-16">
        {/* Hero Section */}
<div className="relative py-20">
  <div className="mx-auto px-8 sm:px-10 lg:px-12">
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-5">
          <h1 className="text-3xl md:text-4xl font-extrabold text-gray-900 leading-tight mb-6 max-w-xl">
            The Software Platform for Your Road Profiler Data
          </h1>
          <p className="text-lg text-gray-600 mb-10 leading-tight max-w-xl">
            From the team at Ames Engineering comes a secure, collaborative platform to manage, analyze, and report on your pavement profile data.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <a href="#get-started" rel="noopener noreferrer" className="bg-red-600 text-white px-4 py-2 font-semibold text-sm flex items-center justify-center  hover:bg-red-700 transition-colors">
          Get Started
            </a>
            <Link 
  to="/feature-highlight" 
  className="border border-gray-300 text-gray-700 px-4 py-2 font-semibold text-sm flex items-center justify-center hover:bg-gray-100 transition-colors"
>
  Explore Features
</Link>
          </div>
        </div>
        
        {/* Right Column - Image Carousel (7/12 width on large screens) */}
      <div className="relative lg:col-span-7">
        {heroImages.map((image, index) => (
          <img 
            key={index}
            src={image} 
            // --- SEO UPDATE: Using descriptive alt text from the array ---
            alt={heroImageAlts[index]} 
            className={`w-full border border-gray-200 transition-opacity duration-1000 ease-in-out ${
              index === currentImageIndex ? 'opacity-100' : 'opacity-0'
            } ${
              // The first image is 'relative' to set the container size
              // All others are 'absolute' to stack on top for fading
              index !== 0 ? 'absolute top-0 left-0' : 'relative'
            }`} 
          />
        ))}
      </div>
    </div>
  </div>
</div>


<section id="legacy" className="bg-gradient-to-br from-red-600 to-red-800 text-white py-12 scroll-mt-20">
      <div className="w-full mx-auto px-8 sm:px-10 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          
          {/* Left Column: Text Content */}
          <div>
            <h2 className="text-2xl font-bold tracking-tight text-white uppercase">
              Built by the Team You Trust
            </h2>
            <p className="text-md text-white mt-6 leading-tight">
              Ames Connect is built by the same engineers at Ames Engineering
              who pioneered inertial profiling technology and have set the
              standard for accuracy and reliability for over 40 years.
            </p>
            <p className="text-md text-white mt-4 leading-tight">
              We are pavement engineers
              with deep FHWA and state agency expertise. This platform is the
              direct result of decades of field experience, designed to solve
              the real-world data challenges you face every day.
            </p>
          </div>

          {/* Right Column: Logo */}
          <div className="flex items-center justify-center">
            <img
              src={amesLogo}
              alt="Ames Engineering Logo"
              className="h-36 w-auto bg-white p-4 rounded"
            />
          </div>

        </div>
      </div>
    </section>

    <HowItWorksSection />



{/* === REVISED 3-COLUMN FEATURES SECTION === */}
<section id="visualization-features" className="py-24 bg-white scroll-mt-20">
  <div className="mx-auto">
    
    <div className="px-8 sm:px-10 lg:px-12 mb-16">
      <h2 className="text-4xl text-gray-900 text-left mb-2">Powerful Visualization Features</h2>
      <p className="text-lg text-gray-600 text-leftleading-tight max-w-3xl">
        Explore the advanced tools that make Ames Connect the ultimate platform for analyzing and visualizing your pavement profile data.
      </p>
    </div>

    {/* 3-Column Grid Layout */}
    <div className="w-full mx-auto mt-10 px-8 sm:px-10 lg:px-12">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
        
        {/* Get first 3 features and map them */}
        {Object.values(features).slice(0, 3).map((feature, index) => (
          <div key={index} className="flex flex-col">
            
            {/* Image */}
            <div className="relative w-full  overflow-hidden border border-gray-200">
              <img 
                src={feature.image} 
                // --- SEO UPDATE: Changed alt tag from feature.title to feature.description ---
                alt={feature.description}
                className="w-full object-cover " // Set a 4:3 aspect ratio for consistency
              />
            </div>
            
            {/* Text Content */}
            <div className="mt-6">
              <h3 className="text-xl font-bold text-gray-900">
                {feature.title}
              </h3>
              <p className="mt-2 text-base text-gray-600 leading-tight">
                {feature.description}
              </p>
            </div>

          </div>
        ))}
      </div>
    </div>
  </div>
</section>

{/* === REVISED 3-COLUMN FEATURES SECTION === */}
{/* --- SEO UPDATE: Fixed duplicate ID. Was "visualization-features" --- */}
<section id="analysis-features" className="pb-24 bg-white scroll-mt-20">
  <div className="mx-auto">
    
    <div className="px-8 sm:px-10 lg:px-12 mb-16">
      <h2 className="text-4xl text-gray-900 text-left mb-2">Analyze and Manage</h2>
      <p className="text-lg text-gray-600 text-left leading-tight max-w-3xl">
        Ames Connect offers a comprehensive suite of tools to help you analyze, manage, and report on your pavement profile data with ease.
      </p>
    </div>

    {/* 3-Column Grid Layout */}
    <div className="w-full mx-auto mt-10 px-8 sm:px-10 lg:px-12">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
        
        {/* Get first 3 features and map them */}
        {Object.values(features).slice(3, 6).map((feature, index) => (
          <div key={index} className="flex flex-col">
            
            {/* Image */}
            <div className="relative w-full  overflow-hidden border border-gray-200">
              <img 
                src={feature.image} 
                // --- SEO UPDATE: Changed alt tag from feature.title to feature.description ---
                alt={feature.description}
                className="w-full object-cover " // Set a 4:3 aspect ratio for consistency
              />
            </div>
            
            {/* Text Content */}
            <div className="mt-6">
              <h3 className="text-xl font-bold text-gray-900">
                {feature.title}
              </h3>
              <p className="mt-2 text-base text-gray-600 leading-tight">
                {feature.description}
              </p>
            </div>

          </div>
        ))}
      </div>
    </div>
  </div>
</section>

{/* Get Started Section */}
<section id="get-started" className="py-12 bg-red-600 scroll-mt-20">
  <div className="max-w-6xl mx-auto px-8">
    
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
      
      {/* Left Column - Content */}
      <div className="text-white">
        <h2 className="text-3xl font-bold mb-3">
          Get Started with Ames Connect
        </h2>
        

        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-2">
            Why schedule onboarding?
          </h3>
          <p className="text-red-50 text-sm">
            Our team will walk you through setup, answer your questions, and ensure you're configured for success from day one.
          </p>
        </div>

        {/* Software Notice - Integrated */}
        <div className="bg-red-700 border-l-4 border-white p-4 mb-6">
          <p className="text-white text-sm font-semibold mb-1">
            Updated Desktop Software Required
          </p>
          <p className="text-red-100 text-sm mb-2">
            Install the latest version of Ames Pro Software before using the platform.
          </p>
          <Link 
            to="/download"
            className="text-white font-semibold text-sm inline-flex items-center hover:underline"
          >
            Download Software
            <ArrowRightIcon className="ml-1 h-4 w-4" />
          </Link>
        </div>

        {/* Support */}
        <p className="text-sm text-red-100">
          Questions?{' '}
          <a href="mailto:sales@amesengineering.com" className="text-white hover:underline font-semibold">
            sales@amesengineering.com
          </a>
          {' • '}
          <a href="tel:+15152928194" className="text-white hover:underline font-semibold">
            515-292-8194
          </a>
        </p>
      </div>

      {/* Right Column - Action Card */}
      <div className="bg-white border border-gray-200 shadow-lg p-6">
        
        {/* Primary Action */}
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-2">
            <h3 className="text-lg font-bold text-gray-900">
              Schedule Onboarding
            </h3>
            <span className="bg-red-600 text-white px-2 py-1 text-xs font-semibold uppercase">
              Recommended
            </span>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            Get personalized setup and training from our team
          </p>
          
          <a 
            href="https://www.amesengineering.com/sales/salesRequest" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="w-full inline-flex items-center justify-center bg-red-600 text-white px-6 py-3 font-semibold hover:bg-red-700 transition-colors"
          >
            Schedule Now
            <ArrowTopRightOnSquareIcon className="ml-2 h-5 w-5" />
          </a>
        </div>

        {/* Divider */}
        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-200"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-4 bg-white text-gray-500">or</span>
          </div>
        </div>

        {/* Secondary Action */}
        <div className="mb-4">
          <h3 className="text-base font-semibold text-gray-900 mb-2">
            Start on Your Own
          </h3>
          <p className="text-sm text-gray-600 mb-3">
            Create an account and explore self-guided resources
          </p>
          
          <Link 
            to="/signup"
            className="w-full inline-flex items-center justify-center border-2 border-gray-300 text-gray-700 px-6 py-2.5 font-semibold hover:bg-gray-50 transition-colors"
          >
            Create Account
          </Link>
        </div>

        {/* Login Link */}
        <p className="text-center text-sm text-gray-600 pt-4 border-t border-gray-100">
          Already have an account?{' '}
          <Link to="/login" className="text-red-600 hover:text-red-700 font-semibold">
            Log in
          </Link>
        </p>
      </div>

    </div>

  </div>
</section>

        
      </main>

      <Footer />
    </div>
  );
}